import { useDisclosure } from "@chakra-ui/react";
import { type BodyColumnDef, DataTable, Link } from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  OrderOrderByColumnEnum,
  useGetOrdersQuery,
} from "@src/__generated__/graphql";
import { GoogleDriveLink } from "@src/components/ui-kit/GoogleDriveLink";
import { SharepointLink } from "@src/components/ui-kit/SharepointLink";
import { ModalOrder } from "@src/components/widgets/Modals/ModalOrder";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { dateTime } from "@src/utils/formatters";
import { filter, last } from "lodash";
import { observer } from "mobx-react-lite";
import React, { type FunctionComponent, useContext, useMemo } from "react";
import type { OrderModel } from "../../models";
import { DocumentsCtx, type StoreDocuments } from "../../StoreDocuments";
import { CellDocuments } from "../CellDocuments";
import { CellFiles } from "../CellFiles";
import s from "../LimitCell.module.css";
import { PopoverRowActions } from "./PopoverRowActions";

/**
 * encapsulate initial data fetch & refetch
 */
function useData(store: StoreDocuments) {
  const { loading, error, refetch } = useGetOrdersQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      ...store.pagination.asParams,
      filters: {
        orderBy: store.orderByMixed.orders.asWhereParam,
        where: store.whereMixed.orders.asGraphQueryParam,
        search: store.searchTerm || undefined,
      },
    },
    onCompleted(data) {
      store.fromGetOrdersQuery(data);
    },
    ...onError(),
  });

  store.refetchOrders = refetch;
  store.error = error;
  store.loading = loading;
}
type TData = {
  id: OrderModel["id"];
  purchaseOrderNo: OrderModel["purchase_order_no"];
  outgoingInvoices: OrderModel["outgoingInvoices"];
  brand: OrderModel["brand"];
  projects: OrderModel["projects"];
  dateOfOrder: OrderModel["date_of_order"];
  files: OrderModel["files"];
  documents: "";
  actions: string;
  cloudUrl: OrderModel["cloudUrl"];
  driveCloudUrl: OrderModel["driveCloudUrl"];
  _model: OrderModel;
};

export const TableOrders = observer(function TableOrders() {
  const store = useContext(DocumentsCtx);

  useData(store);

  const data: TData[] = store.orders.map((r) => ({
    id: r.id,
    purchaseOrderNo: r.purchase_order_no,
    brand: r.brand,
    projects: r.projects,
    dateOfOrder: r.date_of_order,
    files: r.files,
    outgoingInvoices: r.outgoingInvoices,
    documents: "",
    actions: "",
    cloudUrl: r.cloudUrl,
    driveCloudUrl: r.driveCloudUrl,
    _model: r,
  }));

  const columns = useMemo<BodyColumnDef<TData, OrderOrderByColumnEnum>[]>(
    () => [
      {
        id: "purchase-order-no",
        meta: { label: t`PO` },
        header: () => t`PO`,
        cell: ({ row }) => <PurchaseOrderNoCell r={row.original} />,
      },
      {
        id: "outgoing-invoices",
        meta: { label: t`Invoice no` },
        header: () => t`Invoice no`,
        cell: ({ row }) => (
          <div className="flex">
            <p className={s.limitCell}>
              {row.original.outgoingInvoices.map((invoice) => (
                <React.Fragment key={invoice.id}>
                  <Link
                    href={{
                      pathname: "/invoices/outgoing-invoices/detail",
                      query: { id: invoice.id },
                    }}
                  >
                    {invoice.invoice_no}
                  </Link>
                  {last(row.original.outgoingInvoices)?.id === invoice.id
                    ? null
                    : ", "}
                </React.Fragment>
              ))}
            </p>
          </div>
        ),
      },
      {
        id: "brand",
        meta: { label: t`Brand` },
        header: () => t`Brand`,
        orderBy: OrderOrderByColumnEnum.BrandName,
        cell: ({ row }) => (
          <Link
            href={{
              pathname: "/brands/detail",
              query: { id: row.original.brand.id },
            }}
          >
            {row.original.brand.name}
          </Link>
        ),
      },
      {
        id: "projects",
        meta: { label: t`Project/s` },
        header: () => t`Project/s`,
        cell: ({ row }) => (
          <div className="flex">
            <p className={s.limitCell}>
              {row.original.projects.map((project) => (
                <React.Fragment key={project.id}>
                  <span title={project.code}>
                    <Link
                      href={{
                        pathname: "/projects/detail",
                        query: { id: project.id },
                      }}
                    >
                      {`${project.code} – ${project.title.replace(" ", "-")}`}
                    </Link>
                  </span>
                  <br />
                </React.Fragment>
              ))}
            </p>
          </div>
        ),
      },
      {
        id: "date-of-order",
        meta: { label: t`Date of order` },
        header: () => t`Date of order`,
        cell: ({ row }) =>
          row.original.dateOfOrder
            ? dateTime.format(row.original.dateOfOrder)
            : "",
      },
      {
        id: "files",
        meta: { label: t`Files` },
        header: () => t`Files`,
        cell: ({ row }) => <CellFiles files={row.original.files} />,
      },
      {
        id: "documents",
        meta: { label: t`Documents` },
        header: () => t`Documents`,
        cell: ({ row }) => (
          <CellDocuments
            data={{
              download: row.original._model.urls?.download,
              previews: row.original._model.files,
            }}
          />
        ),
      },
      {
        id: "sharepoint",
        meta: { label: t`Sharepoint` },
        header: () => t`Sharepoint`,
        cell: ({ row }) =>
          row.original.cloudUrl && (
            <SharepointLink href={row.original.cloudUrl} />
          ),
      },
      {
        id: "drive",
        meta: { label: t`Google Drive` },
        header: () => t`Google Drive`,
        cell: ({ row }) =>
          row.original.driveCloudUrl && (
            <GoogleDriveLink href={row.original.driveCloudUrl} />
          ),
      },
      {
        id: "actions",
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        cell: ({ row }) => <PopoverRowActions order={row.original._model} />,
      },
    ],
    [],
  );

  const visibleColumns = useMemo(() => {
    if (store.asExternalModule) {
      return filter(
        columns,
        (col) =>
          !col.id ||
          !store.hideColsInExternalModule.otherDocuments.includes(col.id),
      );
    }
    return columns;
  }, [
    columns,
    store.asExternalModule,
    store.hideColsInExternalModule.otherDocuments,
  ]);

  return (
    <DataTable.Body
      tableKey="orders"
      data={data}
      columns={visibleColumns}
      orderedBy={store.orderByMixed.orders.clauses[0]}
      onOrderByChange={store.handleOrdersOrderByChange}
    />
  );
});

const PurchaseOrderNoCell: FunctionComponent<
  React.PropsWithChildren<{ r: TData }>
> = observer(function PurchaseOrderNoCell({ r }) {
  const pageStore = useContext(DocumentsCtx);
  const editModal = useDisclosure();

  return (
    <React.Fragment>
      {can("order_update") ? (
        <button
          type="button"
          onClick={editModal.onOpen}
          className="Polaris-Link outline-none border-none"
        >
          {r.purchaseOrderNo}
        </button>
      ) : (
        <span>{r.purchaseOrderNo}</span>
      )}
      <ModalOrder
        order={r._model}
        onSubmit={pageStore.refetchOrders}
        {...editModal}
      />
    </React.Fragment>
  );
});
